import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ArrowLeft, ArrowRight, Clock, MapPin, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import Header from '../components/site/Header'
import Footer from '../components/site/Footer'
import { useLanguage } from '@/i18n/LanguageContext'
import { CALENDLY_URL } from '../components/site/constants'
import useSEO from '../hooks/useSEO'
import usePrerenderReady from '../hooks/usePrerenderReady'

export default function ItinerariesPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])
  const { t } = useLanguage()
  const copy = t.itinerariesPage
  const [selectedItem, setSelectedItem] = useState<(typeof copy.items)[number] | null>(null)

  useSEO({ title: `${copy.eyebrow} — Fra un viaggio e l’altro`, description: copy.intro })
  usePrerenderReady()

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Header />
      <main className="pt-32">
        <section className="mx-auto w-[90%] max-w-6xl pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full">
            <div className="flex items-center justify-between gap-6">
              <p className="text-sm font-medium uppercase tracking-[0.25em] text-terracotta">{copy.eyebrow}</p>
              <Link to="/" className="group inline-flex shrink-0 items-center gap-3 rounded-full border border-ink/15 bg-white/60 px-5 py-3 text-sm font-semibold text-ink/70 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta/40 hover:bg-white hover:text-terracotta hover:shadow-md">
                <ArrowLeft className="h-4 w-4" /> {copy.backHome}
              </Link>
            </div>
            <h1 className="mt-7 w-full font-display text-5xl leading-tight md:text-7xl">
              {copy.titleBefore} <span className="italic text-terracotta">{copy.titleAccent}</span>
            </h1>
            <p className="mt-7 text-lg leading-relaxed text-ink/70">{copy.intro}</p>
          </motion.div>

          <div className="mt-16 grid gap-7 md:grid-cols-2">
            {copy.items.map((item, index) => (
              <motion.article key={item.title} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ duration: 0.55, delay: index * 0.06 }} className="rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_18px_50px_-25px_rgba(15,43,70,0.28)] md:p-10">
                <div className="flex items-center justify-between gap-4 text-sm text-terracotta">
                  <span className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {t.itinerariesPage.itineraryLabel.toUpperCase()}&nbsp;0{index + 1}</span>
                  <span className="flex items-center gap-2 text-ink/55"><Clock className="h-4 w-4" /> {item.duration}</span>
                </div>
                <img
                  src={item.cover}
                  alt={item.coverAlt}
                  className="mt-7 aspect-[16/9] w-full rounded-2xl object-cover"
                  loading="lazy"
                />
                <h2 className="mt-7 font-display text-3xl leading-tight md:text-4xl">{item.title}</h2>
                <p className="mt-4 text-lg leading-relaxed text-ink/75">{item.description}</p>
                <div className="mt-8 border-t border-ink/10 pt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">{copy.detailsLabel}</p>
                    <p className="mt-3 whitespace-pre-line leading-relaxed text-ink/65">{item.details}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-cream transition-transform hover:scale-105"
                >
                  {copy.discoverMore} <ArrowRight className="h-4 w-4" />
                </button>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="bg-ink px-6 py-20 text-cream md:py-28">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-medium uppercase tracking-[0.25em] text-terracotta">{copy.ctaEyebrow}</p>
            <h2 className="mt-5 font-display text-4xl leading-tight md:text-6xl">{copy.ctaTitle}</h2>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-cream/70">{copy.ctaText}</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="mt-9 inline-flex items-center gap-3 rounded-full bg-terracotta px-7 py-4 font-semibold text-cream transition-transform hover:scale-105">
              {t.common.ctaLabel} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/70 px-5 py-8 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
            >
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-labelledby="itinerary-modal-title"
                className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-cream p-6 shadow-2xl md:p-10"
                initial={{ opacity: 0, y: 24, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.97 }}
                onClick={(event) => event.stopPropagation()}
              >
                <button type="button" onClick={() => setSelectedItem(null)} aria-label={copy.closeModalAria} className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-cream/90 text-ink transition-colors hover:bg-terracotta hover:text-cream">
                  <X className="h-5 w-5" />
                </button>
                <img src={selectedItem.cover} alt={selectedItem.coverAlt} className="aspect-[16/9] w-full rounded-2xl object-cover" />
                <div className="mt-7 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-terracotta">
                  <Clock className="h-4 w-4" /> {selectedItem.duration}
                </div>
                <h2 id="itinerary-modal-title" className="mt-3 font-display text-4xl leading-tight md:text-5xl">{selectedItem.title}</h2>
                <p className="mt-5 text-lg leading-relaxed text-ink/75">{selectedItem.description}</p>
                <div className="mt-8 border-t border-ink/10 pt-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">{copy.detailsLabel}</p>
                  <p className="mt-3 whitespace-pre-line text-lg leading-relaxed text-ink/70">{selectedItem.details}</p>
                </div>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-cream transition-transform hover:scale-105"
                >
                  {t.common.ctaLabel} <ArrowRight className="h-4 w-4" />
                </a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  )
}
